// File: frontend/src/components/admin/tabs/UsersTab.js
//
// Browse every account. Search, sort, filter, bulk actions, and a click through
// to the full detail view.
//
// The mobile story is the point of this file. The old version rendered a
// `<table className="min-w-[640px]">` inside an overflow-x container, so on a
// 375px phone you could see the email column and had to drag sideways for
// everything else — including the two dates you actually came to compare.
//
// Now the same rows render twice from ONE formatter: a stacked card list below
// `md:`, the table from `md:` up. CSS visibility rather than a JS breakpoint,
// so there is no flash on load or jump on resize, and it works on the old
// Chrome the browserslist still targets.
import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Search, RefreshCw, ChevronLeft, ChevronRight, ChevronUp, ChevronDown,
  Users as UsersIcon,
} from 'lucide-react';
import { adminFetch } from '../adminApi';
import {
  Card, Button, IconButton, SectionError, EmptyState, Loading,
} from '../primitives';
import UserDetail, { UserBadges, authLabel } from '../UserDetail';
import {
  formatInstant, formatRelative, formatRelativeDay, formatCalendarDate,
} from '../../../utils/adminDates';
import { logger } from '../../../utils/logger';

const log = logger.child('adminUsers');

const FILTERS = [
  { value: 'all', label: 'All users' },
  { value: 'pro', label: 'Pro' },
  { value: 'free', label: 'Free' },
  { value: 'support_agent', label: 'Support agents' },
  { value: 'admin', label: 'Admins' },
  { value: 'overview_email', label: 'Daily overview email' },
  { value: 'unverified', label: 'Unverified email' },
];

const COLUMNS = [
  { key: 'email', label: 'Email' },
  { key: 'created_at', label: 'Joined' },
  { key: 'habit_count', label: 'Habits', numeric: true },
  { key: 'completion_count', label: 'Completions', numeric: true },
  { key: 'last_active_at', label: 'Last seen' },
  { key: 'last_logged_date', label: 'Last logged' },
];

const BULK_ACTIONS = [
  { value: 'verify', label: 'Mark verified' },
  { value: 'unverify', label: 'Mark unverified' },
  { value: 'grant_pro', label: 'Grant Pro (override)' },
  { value: 'revoke_pro', label: 'Revoke Pro override' },
  { value: 'make_admin', label: 'Make admin' },
  { value: 'revoke_admin', label: 'Revoke admin' },
  { value: 'delete', label: 'Delete accounts' },
];

// One place that decides how a user row reads, consumed by both the card list
// and the table. Keeping this shared is what stops the two renderings drifting
// the way the old "Joined" and "Last active" cells did — one ran through
// toLocaleDateString and the other printed a raw string, three lines apart.
export function formatUserRow(u) {
  return {
    joined: {
      text: formatInstant(u.created_at, { withTime: false }) || '—',
      title: formatInstant(u.created_at) || undefined,
      relative: formatRelative(u.created_at),
    },
    // Server-clock presence. Cannot be earlier than the join date.
    lastSeen: {
      text: u.last_active_at ? formatRelative(u.last_active_at) : 'Never',
      title: u.last_active_at ? formatInstant(u.last_active_at) : 'No activity recorded',
      muted: !u.last_active_at,
    },
    // The habit day they ticked. A date the user picked, so it can legitimately
    // predate the account — labelled "Last logged" rather than "Last active"
    // so that stops looking like a bug.
    lastLogged: {
      text: u.last_logged_date ? formatRelativeDay(u.last_logged_date) : 'Never',
      title: u.last_logged_date ? formatCalendarDate(u.last_logged_date) : 'No habits logged',
      muted: !u.last_logged_date,
    },
    sync: {
      text: u.last_sync_at ? formatRelative(u.last_sync_at) : 'Never synced',
      title: u.last_sync_at ? formatInstant(u.last_sync_at) : 'This client has never registered a device',
    },
    auth: authLabel(u),
  };
}

function Checkbox({ checked, onChange, label }) {
  // w-4 h-4 with a padded hit area: the originals were the browser default
  // (~13px) with no padding, far below a usable touch target.
  return (
    <label className="inline-flex items-center justify-center p-2.5 -m-1 cursor-pointer">
      <input
        type="checkbox"
        aria-label={label}
        checked={checked}
        onChange={onChange}
        className="w-4 h-4 accent-[var(--color-admin-accent)]"
      />
    </label>
  );
}

export default function UsersTab({ apiUrl }) {
  const [query, setQuery] = useState('');
  const [appliedQuery, setAppliedQuery] = useState('');
  const [sort, setSort] = useState('created_at');
  const [order, setOrder] = useState('desc');
  const [filter, setFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const [bulkAction, setBulkAction] = useState('verify');
  const [confirmingBulk, setConfirmingBulk] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);

  // Debounced search: narrows as you type without one request per keystroke.
  useEffect(() => {
    const t = setTimeout(() => { setAppliedQuery(query.trim()); setPage(1); }, 350);
    return () => clearTimeout(t);
  }, [query]);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError('');
    const params = new URLSearchParams({ page: String(page), limit: String(limit), sort, order, filter });
    if (appliedQuery) params.set('q', appliedQuery);
    const url = `${apiUrl}/api/admin/users?${params}`;
    try {
      setData(await adminFetch(url));
    } catch (err) {
      log.error('fetchUsers failed', err, { method: 'GET', url });
      setError(err.message || 'Failed to load users');
    }
    setLoading(false);
  }, [apiUrl, page, limit, sort, order, filter, appliedQuery]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const toggleSort = (key) => {
    if (sort === key) setOrder(order === 'asc' ? 'desc' : 'asc');
    else {
      setSort(key);
      // Text reads naturally A→Z; everything else leads with the biggest/newest.
      setOrder(key === 'email' ? 'asc' : 'desc');
    }
    setPage(1);
  };

  const toggleSelected = (userId) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId); else next.add(userId);
      return next;
    });
    setConfirmingBulk(false);
  };

  const runBulkAction = async () => {
    // Deletion is irreversible, so it takes a second click to confirm.
    if (bulkAction === 'delete' && !confirmingBulk) { setConfirmingBulk(true); return; }
    setBulkBusy(true);
    setConfirmingBulk(false);
    setError('');
    try {
      await adminFetch(`${apiUrl}/api/admin/users/bulk`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: bulkAction, ids: [...selected] }),
      });
      setSelected(new Set());
      await fetchUsers();
    } catch (err) {
      log.error('runBulkAction failed', err, { bulkAction, count: selected.size });
      setError(err.message || 'Bulk action failed');
    }
    setBulkBusy(false);
  };

  const users = useMemo(() => data?.users || [], [data]);
  const total = data?.total || 0;
  const totalPages = data?.totalPages || 1;
  const rangeStart = total === 0 ? 0 : (page - 1) * limit + 1;
  const rangeEnd = Math.min(page * limit, total);
  const allOnPageSelected = users.length > 0 && users.every(u => selected.has(u.id));

  const setPageSelection = (checked) => {
    setSelected(prev => {
      const next = new Set(prev);
      users.forEach(u => { if (checked) next.add(u.id); else next.delete(u.id); });
      return next;
    });
    setConfirmingBulk(false);
  };

  if (selectedUserId != null) {
    return (
      <Card>
        <UserDetail
          apiUrl={apiUrl}
          userId={selectedUserId}
          onBack={() => setSelectedUserId(null)}
          onAgentChange={fetchUsers}
          onFlagsChange={fetchUsers}
          onDeleted={() => { setSelectedUserId(null); fetchUsers(); }}
        />
      </Card>
    );
  }

  return (
    <Card
      title={`Users${total ? ` · ${total.toLocaleString()}` : ''}`}
      icon={UsersIcon}
      tone="accent"
      action={
        <IconButton icon={RefreshCw} label="Refresh users" onClick={fetchUsers} />
      }
    >
      {/* Search + filters. Stacked on a phone; the old version wrapped into
          three ragged rows because the input carried a 220px minimum. */}
      <div className="flex flex-col sm:flex-row gap-2 mb-4">
        <div className="relative flex-1 min-w-0">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-light pointer-events-none" />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by email…"
            aria-label="Search users by email"
            className="w-full pl-9 pr-3 min-h-[44px] border border-divider rounded-xl outline-none focus:ring-2 focus:ring-admin bg-surface-input text-ink-main text-sm"
          />
        </div>
        <div className="flex gap-2">
          <select
            value={filter}
            onChange={(e) => { setFilter(e.target.value); setPage(1); }}
            aria-label="Filter users"
            className="flex-1 sm:flex-none px-3 min-h-[44px] border border-divider rounded-xl text-sm bg-surface-input text-ink-main outline-none focus:ring-2 focus:ring-admin"
          >
            {FILTERS.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
          </select>
          <select
            value={limit}
            onChange={(e) => { setLimit(parseInt(e.target.value, 10)); setPage(1); }}
            aria-label="Results per page"
            className="px-3 min-h-[44px] border border-divider rounded-xl text-sm bg-surface-input text-ink-main outline-none focus:ring-2 focus:ring-admin"
          >
            {[25, 50, 100].map(n => <option key={n} value={n}>{n} / page</option>)}
          </select>
        </div>
      </div>

      <SectionError message={error} onRetry={fetchUsers} />

      {/* Bulk bar */}
      {selected.size > 0 && (
        <div className="bg-surface-input border border-divider rounded-xl px-3 py-3 mb-4 flex flex-col sm:flex-row sm:items-center gap-2 text-sm">
          <span className="font-medium text-ink-main">{selected.size} selected</span>
          <select
            value={bulkAction}
            onChange={(e) => { setBulkAction(e.target.value); setConfirmingBulk(false); }}
            aria-label="Bulk action"
            className="px-3 min-h-[44px] border border-divider rounded-xl text-sm bg-surface-card text-ink-main outline-none focus:ring-2 focus:ring-admin"
          >
            {BULK_ACTIONS.map(a => <option key={a.value} value={a.value}>{a.label}</option>)}
          </select>
          <div className="flex items-center gap-2 flex-wrap">
            <Button
              variant={confirmingBulk ? 'dangerSolid' : bulkAction === 'delete' ? 'danger' : 'primary'}
              onClick={runBulkAction}
              disabled={bulkBusy || selected.size > 100}
            >
              {bulkBusy ? 'Working…'
                : confirmingBulk ? `Really delete ${selected.size} account${selected.size === 1 ? '' : 's'}?`
                : 'Apply'}
            </Button>
            {selected.size > 100 && <span className="text-danger text-xs">Max 100 at a time</span>}
            <Button variant="ghost" onClick={() => { setSelected(new Set()); setConfirmingBulk(false); }}>
              Clear
            </Button>
          </div>
        </div>
      )}

      {loading && !data && <Loading>Loading users…</Loading>}

      {users.length === 0 && !loading && !error && <EmptyState>No users match</EmptyState>}

      {/* ── Phone: stacked cards ─────────────────────────────────────────── */}
      {users.length > 0 && (
        <ul className="md:hidden space-y-2">
          {users.map(u => {
            const row = formatUserRow(u);
            return (
              <li key={u.id} className="border border-divider rounded-xl bg-surface-input">
                <div className="flex items-start gap-1 p-3">
                  <Checkbox
                    checked={selected.has(u.id)}
                    onChange={() => toggleSelected(u.id)}
                    label={`Select ${u.email}`}
                  />
                  <button
                    type="button"
                    onClick={() => setSelectedUserId(u.id)}
                    className="flex-1 min-w-0 text-left"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-medium text-ink-main text-sm break-all">{u.email}</span>
                      <ChevronRight size={16} className="text-ink-light shrink-0" aria-hidden="true" />
                    </div>
                    <div className="flex flex-wrap gap-1 mt-1.5"><UserBadges user={u} /></div>
                    <dl className="grid grid-cols-2 gap-x-3 gap-y-1.5 mt-3 text-xs">
                      <div>
                        <dt className="text-ink-subtle">Joined</dt>
                        <dd className="text-ink-light" title={row.joined.title}>{row.joined.text}</dd>
                      </div>
                      <div>
                        <dt className="text-ink-subtle">Last seen</dt>
                        <dd className={row.lastSeen.muted ? 'text-ink-subtle' : 'text-ink-light'} title={row.lastSeen.title}>
                          {row.lastSeen.text}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-ink-subtle">Habits</dt>
                        <dd className="admin-nums text-ink-light">{u.habit_count}</dd>
                      </div>
                      <div>
                        <dt className="text-ink-subtle">Completions</dt>
                        <dd className="admin-nums text-ink-light">{u.completion_count}</dd>
                      </div>
                      <div>
                        <dt className="text-ink-subtle">Last logged</dt>
                        <dd className={row.lastLogged.muted ? 'text-ink-subtle' : 'text-ink-light'} title={row.lastLogged.title}>
                          {row.lastLogged.text}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-ink-subtle">Auth</dt>
                        <dd className="text-ink-light">{row.auth}</dd>
                      </div>
                    </dl>
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {/* ── Desktop: the table ───────────────────────────────────────────── */}
      {users.length > 0 && (
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-ink-light border-b border-divider">
                <th className="py-2 px-2 w-10">
                  <Checkbox
                    checked={allOnPageSelected}
                    onChange={(e) => setPageSelection(e.target.checked)}
                    label="Select all on this page"
                  />
                </th>
                {COLUMNS.map(col => (
                  <th
                    key={col.key}
                    // aria-sort belongs on the column header, not on the button
                    // inside it — the implicit `button` role does not support it.
                    aria-sort={sort === col.key ? (order === 'asc' ? 'ascending' : 'descending') : 'none'}
                    className={`py-2 px-2 font-medium ${col.numeric ? 'text-right' : ''}`}
                  >
                    <button
                      onClick={() => toggleSort(col.key)}
                      className={`inline-flex items-center gap-1 py-2 hover:text-ink-main ${sort === col.key ? 'text-admin font-semibold' : ''}`}
                    >
                      {col.label}
                      {sort === col.key && (order === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />)}
                    </button>
                  </th>
                ))}
                <th className="py-2 px-2 font-medium">Auth</th>
                <th className="py-2 px-2" />
              </tr>
            </thead>
            <tbody>
              {users.map(u => {
                const row = formatUserRow(u);
                return (
                  <tr
                    key={u.id}
                    onClick={() => setSelectedUserId(u.id)}
                    className="border-b border-divider last:border-0 hover:bg-surface-input cursor-pointer"
                  >
                    <td className="py-2 px-2" onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        checked={selected.has(u.id)}
                        onChange={() => toggleSelected(u.id)}
                        label={`Select ${u.email}`}
                      />
                    </td>
                    <td className="py-2.5 px-2">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-ink-main">{u.email}</span>
                        <UserBadges user={u} />
                      </div>
                    </td>
                    <td className="py-2.5 px-2 text-ink-light whitespace-nowrap" title={row.joined.title}>
                      {row.joined.text}
                    </td>
                    <td className="py-2.5 px-2 text-right admin-nums text-ink-light">{u.habit_count}</td>
                    <td className="py-2.5 px-2 text-right admin-nums text-ink-light">{u.completion_count}</td>
                    <td
                      className={`py-2.5 px-2 whitespace-nowrap ${row.lastSeen.muted ? 'text-ink-subtle' : 'text-ink-light'}`}
                      title={row.lastSeen.title}
                    >
                      {row.lastSeen.text}
                    </td>
                    <td
                      className={`py-2.5 px-2 whitespace-nowrap ${row.lastLogged.muted ? 'text-ink-subtle' : 'text-ink-light'}`}
                      title={row.lastLogged.title}
                    >
                      {row.lastLogged.text}
                    </td>
                    <td className="py-2.5 px-2 text-ink-light whitespace-nowrap">{row.auth}</td>
                    <td className="py-2.5 px-2 text-ink-light"><ChevronRight size={14} /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Why the two date columns differ, said once where it is read. */}
      {users.length > 0 && (
        <p className="text-xs text-ink-subtle mt-3 leading-snug">
          <strong className="text-ink-light">Last seen</strong> is server-recorded presence.{' '}
          <strong className="text-ink-light">Last logged</strong> is the most recent habit day ticked —
          a date the user picks, so it can fall before they joined if they backfilled past days.
        </p>
      )}

      {/* Pagination */}
      <div className="flex flex-col xs:flex-row items-center justify-between gap-3 mt-4 text-sm">
        <span className="text-ink-light text-xs sm:text-sm">
          {total === 0 ? 'No results' : `${rangeStart.toLocaleString()}–${rangeEnd.toLocaleString()} of ${total.toLocaleString()}`}
        </span>
        <div className="flex items-center gap-2">
          <Button variant="secondary" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1} aria-label="Previous page">
            <ChevronLeft size={16} />
          </Button>
          <span className="text-ink-light admin-nums whitespace-nowrap">{page} / {totalPages}</span>
          <Button variant="secondary" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages} aria-label="Next page">
            <ChevronRight size={16} />
          </Button>
        </div>
      </div>
    </Card>
  );
}
